import clsx from "clsx";
import { useId, type ReactNode } from "react";
import type { AssigneeDirectory, CreateCaseInput } from "../../../../shared/contracts.js";
import {
  LIMITS,
  MIN_VEHICLE_YEAR,
  maxVehicleYearAt,
} from "../../../../shared/contracts.js";
import { normalizeName, normalizeVin } from "../../../../shared/formatting.js";
import { describeError } from "../../../ui/AsyncPanel.js";
import { Button } from "../../../ui/Button.js";
import { FormField, TextField } from "../../../ui/FormField.js";
import {
  isBlankLine,
  isCalendarDate,
  LINE_MESSAGES,
  lineFieldKey,
  newLineValues,
  parseWholeNumber,
  toPartLineInput,
  validateLine,
  type OrderFormLineErrors,
  type OrderFormLineValues,
} from "./PartLinesGrid.js";

/**
 * The manual order form (spec §14): its local editing type, the validation
 * that runs in the browser before the server's own, the mapping to the
 * shared `CreateCaseInput`, and the field sections the new-order page and
 * the Parts edit sheet both render.
 *
 * Everything here is text as Parts typed it. Numbers are parsed, blanks are
 * dropped and identifiers are normalized only at the boundary
 * (`toCreateCaseInput`), and the server validates the result again — the
 * browser check exists so a mistake is pointed out beside the field before
 * a request is made, never so that the server can trust the browser.
 *
 * The VIN, the RO number and the technician are optional (owner decision
 * 2026-09-17; spec §5 had the VIN and the technician required) and, unlike
 * the "Optional details" section, carry no "Optional" tag: they are simply
 * not required. A VIN is checked for its shape only once something is
 * typed; the technician select's first choice is "Not assigned".
 *
 * The part lines — their editing type, validation, catalog auto-fill,
 * master ETA and the grid itself — live in `PartLinesGrid.tsx` since the
 * wholesale order form shares them (wholesale spec §4.3); the line helpers
 * are re-exported from here so the service form's callers read as before.
 */

export {
  clearChangedLineMarks,
  isCalendarDate,
  lineFieldKey,
  MasterEtaField,
  newClientId,
  newLineValues,
  parseWholeNumber,
  toEtaInput,
  toPartLineInput,
  validateLine,
  withMasterEta,
  withoutBlankTrailingLines,
  type MasterEtaFieldProps,
  type OrderFormLineErrors,
  type OrderFormLineFieldKey,
  type OrderFormLineValues,
} from "./PartLinesGrid.js";

// ---------------------------------------------------------------------------
// Editing types
// ---------------------------------------------------------------------------

export type OrderFormValues = {
  vin: string;
  roNumber: string;
  customerName: string;
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  advisorId: string;
  technicianId: string;
  appointmentDate: string;
  vendorOrderReference: string;
  orderNote: string;
  /** "ETA for all parts" as typed (`YYYY-MM-DD` or blank). */
  masterEta: string;
  /** The customer's car is at the dealership now. */
  carInShop: boolean;
  partLines: OrderFormLineValues[];
};

/** The header fields — everything except the part lines. */
export type OrderFormHeaderValues = Omit<OrderFormValues, "partLines">;
export type OrderFormFieldKey = keyof OrderFormHeaderValues;

export type OrderFormErrors = Readonly<{
  fields: Partial<Record<OrderFormFieldKey, string>>;
  /** Keyed by the line's `clientId`. */
  lines: Readonly<Record<string, OrderFormLineErrors>>;
  isValid: boolean;
}>;

export const NO_ERRORS: OrderFormErrors = { fields: {}, lines: {}, isValid: true };

/** The header field names, for mapping server field errors by path. */
export const ORDER_FORM_HEADER_KEYS: readonly OrderFormFieldKey[] = [
  "vin",
  "roNumber",
  "customerName",
  "vehicleYear",
  "vehicleMake",
  "vehicleModel",
  "advisorId",
  "technicianId",
  "appointmentDate",
  "vendorOrderReference",
  "orderNote",
  "masterEta",
];

export function isOrderFormFieldKey(value: string): value is OrderFormFieldKey {
  return (ORDER_FORM_HEADER_KEYS as readonly string[]).includes(value);
}

export function emptyOrderFormValues(): OrderFormValues {
  return {
    vin: "",
    roNumber: "",
    customerName: "",
    vehicleYear: "",
    vehicleMake: "",
    vehicleModel: "",
    advisorId: "",
    technicianId: "",
    appointmentDate: "",
    vendorOrderReference: "",
    orderNote: "",
    masterEta: "",
    carInShop: false,
    partLines: [newLineValues()],
  };
}

/**
 * True while nothing has been typed or chosen — the state a fresh form starts in.
 *
 * Each header field is compared with its OWN empty value rather than with "".
 * The old `every((value) => value === "")` assumed every field was a string;
 * the first one that is not (`carInShop`, false when fresh) made every
 * untouched form look edited, so leaving a blank New Order page raised the
 * unsaved-changes warning. This form of the check holds for the next
 * non-string field too.
 */
export function isPristineOrderForm(values: OrderFormValues): boolean {
  const { partLines, ...header } = values;
  const { partLines: _freshLines, ...fresh } = emptyOrderFormValues();
  const keys = Object.keys(fresh) as (keyof typeof fresh)[];
  return (
    keys.every((key) => header[key] === fresh[key]) &&
    partLines.length === 1 &&
    isBlankLine(partLines[0]!)
  );
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

export const ORDER_FORM_MESSAGES = {
  vin: "Enter the full 17-character VIN or exactly its last eight characters.",
  roNumber: `Use ${LIMITS.ro.min}–${LIMITS.ro.max} characters.`,
  customerName: "Enter the customer's name.",
  customerNameLength: `Use ${LIMITS.name.min}–${LIMITS.name.max} characters.`,
  vehicleYear: (max: number) => `Enter a year from ${MIN_VEHICLE_YEAR} through ${max}.`,
  vehicleMake: "Enter the vehicle make.",
  vehicleModel: "Enter the vehicle model.",
  vehicleTextLength: `Use ${LIMITS.vehicleText.min}–${LIMITS.vehicleText.max} characters.`,
  advisorId: "Choose the service advisor.",
  appointmentDate: "Enter a valid date.",
  vendorOrderReference: `Use up to ${LIMITS.vendorReference.max} characters.`,
  orderNote: `Use up to ${LIMITS.note.max} characters.`,
  ...LINE_MESSAGES,
  reason: `Give a reason of ${LIMITS.reason.min}–${LIMITS.reason.max} characters.`,
} as const;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** The shape rule, applied only once something was typed: a blank VIN is no VIN. */
function vinError(vin: string): string | undefined {
  if (vin.trim() === "") return undefined;
  const length = normalizeVin(vin).length;
  return length === 8 || length === 17 ? undefined : ORDER_FORM_MESSAGES.vin;
}

function lengthError(
  value: string,
  min: number,
  max: number,
  missing: string,
  wrongLength: string,
): string | undefined {
  const length = normalizeName(value).length;
  if (length === 0) return min > 0 ? missing : undefined;
  return length < min || length > max ? wrongLength : undefined;
}

/** A reason for a correction, override, exception or cancellation: 5–500 characters trimmed. */
export function reasonError(reason: string): string | undefined {
  const length = reason.trim().length;
  return length < LIMITS.reason.min || length > LIMITS.reason.max
    ? ORDER_FORM_MESSAGES.reason
    : undefined;
}

export function validateHeader(
  values: OrderFormHeaderValues,
  now: Date,
): Partial<Record<OrderFormFieldKey, string>> {
  const fields: Partial<Record<OrderFormFieldKey, string>> = {};
  const maxYear = maxVehicleYearAt(now);

  const vin = vinError(values.vin);
  if (vin) fields.vin = vin;

  if (values.roNumber.trim() !== "") {
    const length = normalizeName(values.roNumber).length;
    if (length < LIMITS.ro.min || length > LIMITS.ro.max) fields.roNumber = ORDER_FORM_MESSAGES.roNumber;
  }

  const customer = lengthError(
    values.customerName,
    LIMITS.name.min,
    LIMITS.name.max,
    ORDER_FORM_MESSAGES.customerName,
    ORDER_FORM_MESSAGES.customerNameLength,
  );
  if (customer) fields.customerName = customer;

  const year = parseWholeNumber(values.vehicleYear);
  if (year === null || year < MIN_VEHICLE_YEAR || year > maxYear) {
    fields.vehicleYear = ORDER_FORM_MESSAGES.vehicleYear(maxYear);
  }

  const make = lengthError(
    values.vehicleMake,
    LIMITS.vehicleText.min,
    LIMITS.vehicleText.max,
    ORDER_FORM_MESSAGES.vehicleMake,
    ORDER_FORM_MESSAGES.vehicleTextLength,
  );
  if (make) fields.vehicleMake = make;
  const model = lengthError(
    values.vehicleModel,
    LIMITS.vehicleText.min,
    LIMITS.vehicleText.max,
    ORDER_FORM_MESSAGES.vehicleModel,
    ORDER_FORM_MESSAGES.vehicleTextLength,
  );
  if (model) fields.vehicleModel = model;

  if (values.advisorId === "") fields.advisorId = ORDER_FORM_MESSAGES.advisorId;

  if (values.appointmentDate.trim() !== "" && !isCalendarDate(values.appointmentDate.trim())) {
    fields.appointmentDate = ORDER_FORM_MESSAGES.appointmentDate;
  }
  if (values.vendorOrderReference.trim().length > LIMITS.vendorReference.max) {
    fields.vendorOrderReference = ORDER_FORM_MESSAGES.vendorOrderReference;
  }
  if (values.orderNote.trim().length > LIMITS.note.max) {
    fields.orderNote = ORDER_FORM_MESSAGES.orderNote;
  }
  if (values.masterEta.trim() !== "" && !isCalendarDate(values.masterEta.trim())) {
    fields.masterEta = ORDER_FORM_MESSAGES.masterEta;
  }
  return fields;
}

/** Validates the whole form at `now` (the vehicle-year ceiling depends on it). */
export function validateOrderForm(values: OrderFormValues, now: Date): OrderFormErrors {
  const fields = validateHeader(values, now);
  const lines: Record<string, OrderFormLineErrors> = {};
  for (const line of values.partLines) {
    const errors = validateLine(line);
    if (Object.keys(errors).length > 0) lines[line.clientId] = errors;
  }
  return {
    fields,
    lines,
    isValid: Object.keys(fields).length === 0 && Object.keys(lines).length === 0,
  };
}

/**
 * Maps a server 422's `fieldErrors` (dotted paths: `vin`,
 * `partLines.0.eta`, `partLines.1.quantityOrdered`) onto the form. Paths
 * the form has no field for are returned so the caller can show them as a
 * general message rather than lose them.
 */
export function orderFormErrorsFromServer(
  fieldErrors: Readonly<Record<string, readonly string[]>>,
  values: OrderFormValues,
  options: { linePrefix?: string; headerPrefix?: string } = {},
): { errors: OrderFormErrors; unmapped: string[] } {
  const linePrefix = options.linePrefix ?? "partLines.";
  const headerPrefix = options.headerPrefix ?? "";
  const fields: Partial<Record<OrderFormFieldKey, string>> = {};
  const lines: Record<string, OrderFormLineErrors> = {};
  const unmapped: string[] = [];

  for (const [path, messages] of Object.entries(fieldErrors)) {
    const message = messages.find((text) => text.length > 0) ?? "Check this field.";
    const headerKey = path.startsWith(headerPrefix) ? path.slice(headerPrefix.length) : null;
    if (headerKey !== null && isOrderFormFieldKey(headerKey)) {
      fields[headerKey] = message;
      continue;
    }
    if (path.startsWith(linePrefix)) {
      const [indexText, field] = path.slice(linePrefix.length).split(".");
      const index = Number(indexText);
      const line = Number.isInteger(index) ? values.partLines[index] : undefined;
      const key = lineFieldKey(field);
      if (line && key) {
        lines[line.clientId] = { ...lines[line.clientId], [key]: message };
        continue;
      }
    }
    unmapped.push(message);
  }
  return {
    errors: {
      fields,
      lines,
      isValid: Object.keys(fields).length === 0 && Object.keys(lines).length === 0,
    },
    unmapped,
  };
}

// ---------------------------------------------------------------------------
// Mapping to the shared contract
// ---------------------------------------------------------------------------

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/**
 * The shared, validated contract from the form's text. Optional fields left
 * blank are omitted (not sent as empty strings) — the VIN and the technician
 * among them; the override reason is included only when resubmitting after
 * a duplicate warning.
 */
export function toCreateCaseInput(
  values: OrderFormValues,
  options: { duplicateOverrideReason?: string } = {},
): CreateCaseInput {
  const vin = blankToUndefined(values.vin);
  const technicianId = blankToUndefined(values.technicianId);
  const roNumber = blankToUndefined(values.roNumber);
  const appointmentDate = blankToUndefined(values.appointmentDate);
  const vendorOrderReference = blankToUndefined(values.vendorOrderReference);
  const orderNote = blankToUndefined(values.orderNote);
  const masterEta = blankToUndefined(values.masterEta);
  const duplicateOverrideReason = blankToUndefined(options.duplicateOverrideReason ?? "");
  return {
    ...(vin !== undefined ? { vin } : {}),
    customerName: values.customerName.trim(),
    vehicleYear: parseWholeNumber(values.vehicleYear) ?? Number.NaN,
    vehicleMake: values.vehicleMake.trim(),
    vehicleModel: values.vehicleModel.trim(),
    advisorId: values.advisorId,
    ...(technicianId !== undefined ? { technicianId } : {}),
    ...(roNumber !== undefined ? { roNumber } : {}),
    ...(appointmentDate !== undefined ? { appointmentDate } : {}),
    ...(vendorOrderReference !== undefined ? { vendorOrderReference } : {}),
    ...(orderNote !== undefined ? { orderNote } : {}),
    ...(masterEta !== undefined ? { masterEta } : {}),
    // Sent only when ticked: absent means "not in the shop" on the server.
    ...(values.carInShop ? { carInShop: true } : {}),
    ...(duplicateOverrideReason !== undefined ? { duplicateOverrideReason } : {}),
    partLines: values.partLines.map(toPartLineInput),
  };
}

// ---------------------------------------------------------------------------
// Field sections (shared by the new-order page and the edit sheet)
// ---------------------------------------------------------------------------

export interface HeaderFieldsProps {
  values: OrderFormHeaderValues;
  errors: Partial<Record<OrderFormFieldKey, string>>;
  onChange: (patch: Partial<OrderFormHeaderValues>) => void;
  disabled?: boolean;
  /** Prefix for control ids, so two forms on one page never collide. */
  idPrefix: string;
}

/** Section 1 — Vehicle and visit. */
export function VehicleAndVisitFields({ values, errors, onChange, disabled, idPrefix }: HeaderFieldsProps) {
  const id = (name: string) => `${idPrefix}-${name}`;
  return (
    <div className="order-form__grid">
      <TextField
        id={id("vin")}
        label="VIN or last eight"
        hint="The full 17-character VIN, or exactly its last eight characters."
        optional="untagged"
        error={errors.vin ?? null}
        value={values.vin}
        onChange={(event) => onChange({ vin: event.target.value })}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        maxLength={40}
        disabled={disabled}
        fieldClassName="order-form__field--wide"
      />
      <TextField
        id={id("roNumber")}
        label="RO number"
        hint="Can be attached later."
        optional="untagged"
        error={errors.roNumber ?? null}
        value={values.roNumber}
        onChange={(event) => onChange({ roNumber: event.target.value })}
        autoComplete="off"
        spellCheck={false}
        maxLength={LIMITS.ro.max + 10}
        disabled={disabled}
      />
      <TextField
        id={id("customerName")}
        label="Customer name"
        error={errors.customerName ?? null}
        value={values.customerName}
        onChange={(event) => onChange({ customerName: event.target.value })}
        autoComplete="off"
        maxLength={LIMITS.name.max + 10}
        disabled={disabled}
        fieldClassName="order-form__field--wide"
      />
      <TextField
        id={id("vehicleYear")}
        label="Vehicle year"
        type="number"
        inputMode="numeric"
        min={MIN_VEHICLE_YEAR}
        step={1}
        error={errors.vehicleYear ?? null}
        value={values.vehicleYear}
        onChange={(event) => onChange({ vehicleYear: event.target.value })}
        disabled={disabled}
      />
      <TextField
        id={id("vehicleMake")}
        label="Vehicle make"
        error={errors.vehicleMake ?? null}
        value={values.vehicleMake}
        onChange={(event) => onChange({ vehicleMake: event.target.value })}
        autoComplete="off"
        maxLength={LIMITS.vehicleText.max + 10}
        disabled={disabled}
      />
      <TextField
        id={id("vehicleModel")}
        label="Vehicle model"
        error={errors.vehicleModel ?? null}
        value={values.vehicleModel}
        onChange={(event) => onChange({ vehicleModel: event.target.value })}
        autoComplete="off"
        maxLength={LIMITS.vehicleText.max + 10}
        disabled={disabled}
      />
      <CarInShopField
        id={id("carInShop")}
        checked={values.carInShop}
        disabled={disabled}
        onChange={(carInShop) => onChange({ carInShop })}
      />
    </div>
  );
}

/**
 * "Car in shop" — the customer's car is at the dealership now.
 *
 * A tile rather than a bare checkbox: this form runs on a Surface Pro at the
 * parts counter, where a 20px box is a poor touch target. The whole tile is
 * the label, so a tap anywhere on it toggles the box. The hint says what the
 * box DOES, because its effect happens somewhere else — on the printed order
 * receipt and in the parts list — and nothing on this form would show it.
 */
export function CarInShopField({
  id,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  return (
    <div className="order-form__field--wide">
      <label className="car-in-shop" htmlFor={id}>
        <input
          id={id}
          type="checkbox"
          className="car-in-shop__checkbox"
          checked={checked}
          disabled={disabled}
          aria-labelledby={labelId}
          aria-describedby={hintId}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="car-in-shop__text">
          <span id={labelId} className="car-in-shop__label">
            Car in shop
          </span>
          <span id={hintId} className="car-in-shop__hint">
            The customer&rsquo;s car is here now. Prints in large type at the top of the order
            receipt so it gets pulled first.
          </span>
        </span>
      </label>
    </div>
  );
}

export interface PeopleFieldsProps extends HeaderFieldsProps {
  assignees: AssigneeDirectory | undefined;
  assigneesLoading: boolean;
  assigneesError: unknown;
  onRetryAssignees: () => void;
  /**
   * People to keep selectable even though the server no longer lists them —
   * the case's current advisor or technician after deactivation — so an edit
   * can leave the assignment alone. A case with no technician has none to keep.
   */
  currentAdvisor?: { id: string; displayName: string };
  currentTechnician?: { id: string; displayName: string } | null;
}

/**
 * The server's options plus the case's current person when the list does
 * not carry them: before the list has loaded they are simply the one known
 * option; once it has, their absence means they were deactivated.
 */
function withCurrent(
  options: readonly { id: string; displayName: string }[],
  current: { id: string; displayName: string } | null | undefined,
  loaded: boolean,
): readonly { id: string; displayName: string; inactive?: boolean }[] {
  if (!current || options.some((option) => option.id === current.id)) return options;
  return loaded ? [{ ...current, inactive: true }, ...options] : [current, ...options];
}

/**
 * Section 2 — People: the assigned advisor and technician, from the server's
 * list. The advisor is required and opens on "Choose…"; the technician is
 * optional and opens on "Not assigned", which stays a valid choice.
 */
export function PeopleFields({
  values,
  errors,
  onChange,
  disabled,
  idPrefix,
  assignees,
  assigneesLoading,
  assigneesError,
  onRetryAssignees,
  currentAdvisor,
  currentTechnician,
}: PeopleFieldsProps) {
  const id = (name: string) => `${idPrefix}-${name}`;
  const noticeId = useId();
  const loaded = assignees !== undefined;
  const advisors = withCurrent(assignees?.advisors ?? [], currentAdvisor, loaded);
  const technicians = withCurrent(assignees?.technicians ?? [], currentTechnician, loaded);
  const unavailable = !assignees && !assigneesLoading;

  const select = (
    name: "advisorId" | "technicianId",
    label: string,
    options: readonly { id: string; displayName: string; inactive?: boolean }[],
    empty: string,
  ) => (
    <FormField
      id={id(name)}
      label={label}
      optional={name === "technicianId" ? "untagged" : false}
      error={errors[name] ?? null}
    >
      {(control) => (
        <select
          {...control}
          className="text-input select-input"
          data-size="desktop"
          value={values[name]}
          onChange={(event) => onChange({ [name]: event.target.value })}
          disabled={disabled || unavailable}
        >
          <option value="">{assigneesLoading ? "Loading…" : empty}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.inactive ? `${option.displayName} (inactive)` : option.displayName}
            </option>
          ))}
        </select>
      )}
    </FormField>
  );

  return (
    <div className="order-form__grid">
      {select("advisorId", "Service advisor", advisors, "Choose…")}
      {select("technicianId", "Technician", technicians, "Not assigned")}
      {unavailable ? (
        <div className="order-form__notice" id={noticeId} role="status">
          <p className="order-form__notice-text">
            Couldn't load the advisors and technicians.{" "}
            {assigneesError ? describeError(assigneesError).description : ""}
          </p>
          <Button variant="secondary" onClick={onRetryAssignees}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Section 4 — Optional details. */
export function OptionalDetailsFields({ values, errors, onChange, disabled, idPrefix }: HeaderFieldsProps) {
  const id = (name: string) => `${idPrefix}-${name}`;
  return (
    <div className="order-form__grid">
      <TextField
        id={id("appointmentDate")}
        label="Appointment date"
        type="date"
        optional
        error={errors.appointmentDate ?? null}
        value={values.appointmentDate}
        onChange={(event) => onChange({ appointmentDate: event.target.value })}
        disabled={disabled}
      />
      <TextField
        id={id("vendorOrderReference")}
        label="Vendor or order reference"
        optional
        error={errors.vendorOrderReference ?? null}
        value={values.vendorOrderReference}
        onChange={(event) => onChange({ vendorOrderReference: event.target.value })}
        autoComplete="off"
        maxLength={LIMITS.vendorReference.max + 10}
        disabled={disabled}
      />
      <FormField
        id={id("orderNote")}
        label="Order note"
        optional
        error={errors.orderNote ?? null}
        className="order-form__field--wide"
      >
        {(control) => (
          <textarea
            {...control}
            className="text-input textarea-input"
            data-size="desktop"
            rows={3}
            value={values.orderNote}
            onChange={(event) => onChange({ orderNote: event.target.value })}
            maxLength={LIMITS.note.max + 10}
            disabled={disabled}
          />
        )}
      </FormField>
    </div>
  );
}

/**
 * A titled section of the form; the title is the section's accessible name.
 * Level 2 on the page (under its h1), level 3 inside the edit sheet (under
 * the dialog's h2).
 */
export function OrderFormSection({
  title,
  description,
  level = 2,
  className,
  children,
}: {
  title: string;
  description?: ReactNode;
  level?: 2 | 3;
  className?: string;
  children: ReactNode;
}) {
  const titleId = useId();
  const Heading = level === 3 ? "h3" : "h2";
  return (
    <section className={clsx("order-form__section", className)} aria-labelledby={titleId}>
      <header className="order-form__section-header">
        <Heading id={titleId} className="order-form__section-title">
          {title}
        </Heading>
        {description ? <p className="order-form__section-description">{description}</p> : null}
      </header>
      {children}
    </section>
  );
}

/**
 * Moves focus to the first control the form marked invalid, in document
 * order — the one nearest the top of the page.
 */
export function focusFirstInvalid(root: HTMLElement | null): boolean {
  const target = root?.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!target) return false;
  target.focus();
  return true;
}
